import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { DismissibleAlertVariation } from '../components/DismissibleAlert';

export interface AlertProps {
  title: string;
  message?: string;
  dismissInSeconds?: number | null;
}

export interface AlertItem {
  id: string;
  title: string;
  message: string;
  variation: DismissibleAlertVariation;
}

interface AlertsContextValue {
  alerts: AlertItem[];
  clearAlerts: () => void;
  dismissAlert: (id: string) => void;
  errorAlert: (alertProps: AlertProps) => string;
  successAlert: (alertProps: AlertProps) => string;
  warnAlert: (alertProps: AlertProps) => string;
}

const AlertsContext = createContext<AlertsContextValue | undefined>(undefined);

interface AlertsProviderProps {
  children: ReactNode;
}

export function AlertsProvider({ children }: AlertsProviderProps) {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const timeoutMapRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const dismissAlert = useCallback((id: string) => {
    const timeoutId = timeoutMapRef.current[id];
    if (timeoutId) {
      clearTimeout(timeoutId);
      delete timeoutMapRef.current[id];
    }

    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const clearAlerts = useCallback(() => {
    const timeoutIds = Object.values(timeoutMapRef.current);
    timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    timeoutMapRef.current = {};
    setAlerts([]);
  }, []);

  const createAlert = useCallback(
    (variation: DismissibleAlertVariation, alertProps: AlertProps) => {
      const { dismissInSeconds = null, message = '', title } = alertProps;
      const id = globalThis.crypto.randomUUID();
      setAlerts((prev) => [...prev, { id, message, title, variation }]);

      if (dismissInSeconds && dismissInSeconds > 0) {
        const timeoutId = globalThis.setTimeout(() => {
          dismissAlert(id);
        }, dismissInSeconds * 1000);
        timeoutMapRef.current[id] = timeoutId;
      }

      return id;
    },
    [dismissAlert],
  );

  const successAlert = useCallback((alertProps: AlertProps) => createAlert('success', alertProps), [createAlert]);

  const warnAlert = useCallback((alertProps: AlertProps) => createAlert('warn', alertProps), [createAlert]);

  const errorAlert = useCallback((alertProps: AlertProps) => createAlert('error', alertProps), [createAlert]);

  useEffect(() => {
    return () => {
      const timeoutIds = Object.values(timeoutMapRef.current);
      timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  const value = useMemo<AlertsContextValue>(
    () => ({
      alerts,
      clearAlerts,
      dismissAlert,
      errorAlert,
      successAlert,
      warnAlert,
    }),
    [alerts, clearAlerts, dismissAlert, errorAlert, successAlert, warnAlert],
  );

  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
}

export function useAlerts(): AlertsContextValue {
  const context = useContext(AlertsContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertsProvider');
  }

  return context;
}
